/**
 * @fileoverview Login mutation hook.
 * @description Wraps the login API call in a TanStack mutation for loading/error state. On
 * success, valid credentials only open a two-factor challenge (not a session) — this stores that
 * challenge and navigates to /two-factor via the shared openTwoFactorChallenge helper (also used
 * by useAcceptInvitation, which reaches this same post-login state after a freshly-accepted
 * invitation); useVerifyTwoFactor is what actually signs the user in once the emailed code is
 * confirmed.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

import {
  loginRequest,
  type LoginRequest,
  type LoginResponse,
} from '@/features/auth/services/auth.service'
import { openTwoFactorChallenge } from '@/shared/lib/auth/two-factor-challenge.utils'
import { useTwoFactorChallenge } from '@/shared/lib/auth/useTwoFactorChallenge'

import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Submits login credentials and, on success, stores the resulting two-factor
 * challenge and navigates to /two-factor.
 * @returns {ReturnType<typeof useMutation<LoginResponse, ApiError, LoginRequest>>} The mutation
 * object (mutate, isPending, error, ...).
 */
export function useLogin() {
  const { setChallenge } = useTwoFactorChallenge()
  const navigate = useNavigate()

  return useMutation<LoginResponse, ApiError, LoginRequest>({
    mutationFn: loginRequest,
    onSuccess: (response, variables) => {
      openTwoFactorChallenge(setChallenge, navigate, variables.identifier, response)
    },
  })
}
